import { takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LatestLeadsTableComponent } from '@dashboard/components/latest-leads-table/latest-leads-table.component';
import { ScheduleTableComponent } from '@dashboard/components/schedule-table/schedule-table.component';
import { DashboardViewOptions, IDashboardViewForm, ScheduleViewOptions } from '@dashboard/models/dashboard';
import { LayoutService } from '@layout/services/layout/layout.service';
import { ILeadRequest } from '@leads/shared/models/firebase/lead-request.model';
import { ILeadAssessmentWithLeadRequest } from '@leads/shared/models/firebase/scheduled-assessment.model';
import { LeadRequestService } from '@leads/shared/services/lead-request/lead-request.service';
import { IncompleteOrganisationBannerComponent } from '@organisations/components/banners/incomplete-organisation-banner/incomplete-organisation-banner.component';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { IOrganisation } from '@organisations/models/organisations';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { OrganisationUserService } from '@organisations/services/organisation-user/organisation-user.service';
import { ScheduleService } from '@schedule/services/schedule/schedule.service';
import { RadioGroupControlComponent } from '@shared/components/forms/radio-group-control/radio-group-control.component';
import { PageTitleComponent } from '@shared/components/sections/page-title/page-title.component';
import { IRadioOption } from '@shared/models/form-controls';
import { IStepNavigation, IStepNavigatorConfig, NavButtonStyle, StepDisplay, StepModes, StepUI } from '@shared/models/step-navigator';

@Component({
	selector: 'app-dashboard',
	imports: [
		PageTitleComponent,
		CommonModule,
		IncompleteOrganisationBannerComponent,
		LatestLeadsTableComponent,
		ScheduleTableComponent,
		RadioGroupControlComponent,
		FormsModule,
		ReactiveFormsModule,
	],
	templateUrl: './dashboard.component.html',
	styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
	private organisationUserService = inject(OrganisationUserService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private leadRequestService = inject(LeadRequestService);
	private scheduleService = inject(ScheduleService);

	private layoutService = inject(LayoutService);

	isMobileSize = true;
	isOwner = false;

	destroyed$ = new EventEmitter<void>();
	noOrganisationAvailable: boolean;
	selectedOrganisation: IOrganisationAndOrganisationUser | null = null;

	latestLeads: Array<ILeadRequest> = [];
	assessments: Array<ILeadAssessmentWithLeadRequest> = [];
	leadsLoading = true;
	assessmentsLoading = true;
	employeeIdFilter: string | null | undefined = null;

	radioOptions: Array<IRadioOption> = [
		{ label: 'Leads', value: DashboardViewOptions.LEADS, id: 'leads' },
		{ label: 'Schedule', value: DashboardViewOptions.SCHEDULE, id: 'schedule' },
	];

	form = new FormGroup<IDashboardViewForm>({
		view: new FormControl<DashboardViewOptions>(DashboardViewOptions.LEADS, { nonNullable: true, validators: [Validators.required] }),
	});

	navigationConfig: IStepNavigatorConfig = {
		ui: StepUI.WIZARD,
		mode: StepModes.NG_SWITCH,
		displayType: StepDisplay.TABS,
		navButtonStyle: NavButtonStyle.CHEVRON,
		showStepChevrons: false,
		showStepNumbers: false,
		showNavButtons: false,
		steps: [
			{
				title: {
					full: 'Today',
					tabs: 'Today',
				},
				icon: 'bi-calendar-day',
				switchCase: ScheduleViewOptions.TODAY,
			},
			{
				title: {
					full: 'Week',
					tabs: 'Week',
				},
				icon: 'bi-calendar-week',
				switchCase: ScheduleViewOptions.WEEK,
			},
		],
	};

	selectedStep: IStepNavigation = this.navigationConfig.steps[0];

	DashboardViewOptions = DashboardViewOptions;

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		this.organisationUserService
			.organisationsByCurrentUser()
			.pipe(takeUntil(this.destroyed$))
			.subscribe((organisations) => {
				this.noOrganisationAvailable = !organisations.length;
			});

		this.organisationSelectedService.selectedOrganisation.pipe(takeUntil(this.destroyed$)).subscribe((selectedOrganisation) => {
			if (!selectedOrganisation) {
				this.selectedOrganisation = null;
				return;
			}
			this.selectedOrganisation = selectedOrganisation;
			const accessLevel = selectedOrganisation?.organisationUser.accessLevel;
			this.isOwner = accessLevel && [OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(accessLevel);
			this.employeeIdFilter = this.isOwner ? null : this.selectedOrganisation?.organisationUser.user?.id;

			this.fetchNewLeads();
			this.fetchCurrentAssessments(this.selectedStep);
		});
	}

	fetchNewLeads(): void {
		this.leadsLoading = true;
		this.leadRequestService
			.getLatestNewLeadRequests(this.selectedOrganisation!.organisationUser.organisation.id)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((leads) => {
				this.latestLeads = leads;
				this.leadsLoading = false;
			});
	}

	fetchCurrentAssessments(step: IStepNavigation): void {
		this.assessmentsLoading = true;
		const startDate = new Date(new Date().setHours(0, 0, 0, 0));
		const endDate = new Date(new Date().setHours(23, 59, 59, 999));
		if (step.switchCase === ScheduleViewOptions.WEEK) {
			endDate.setDate(endDate.getDate() + 7);
		}

		this.scheduleService
			.getScheduledAssessments(
				this.selectedOrganisation?.organisationUser.organisation as DocumentReference<IOrganisation>,
				startDate,
				endDate,
				this.employeeIdFilter,
			)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((assessments) => {
				this.assessments = assessments;
				this.assessmentsLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	get selectedMobileView(): DashboardViewOptions {
		return this.form.controls.view.value;
	}
}
